import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { combineLatest, from, NEVER, shareReplay } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { getCalibrationData } from '../../../../../cross-team-shared/misc-utils/calibration-data';
import {
  isImageAnnotationOutput,
  isPointCloudOutput,
  isSensorFusionOutput,
  isVideoOutput,
} from '../../../../../cross-team-shared/misc-utils/output-utils';
import { getTotalFusedOutputs } from '../../../../../cross-team-shared/misc-utils/project-utils';
import { FusionManagerFactory } from '../../../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { CVToolbarKeyboardShortcutService } from '../../../../outputs/annotation/annotation-shared/toolbars/static-toolbar/cv-toolbar-keyboard-shortcut.service';
import { FeatureFlagService } from '../../../../shared/misc-services/feature-flag.service';
import { TaskWorkspaceStore } from '../../../state/task-workspace-store';
import { getOutputByName } from '../sensor-fusion-output.utils';

@Component({
  selector: 'h3-cv-toolbar',
  templateUrl: './cv-toolbar.component.html',
  styleUrl: './cv-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CVToolbarComponent {
  readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly fusionManagerFactory = inject(FusionManagerFactory);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly selectedOutput$ = this.taskWorkspaceStore(
    'selectedOutputName',
  ).$.pipe(
    filter(isDefined),
    map((splitViewOutputName) => {
      const { project } = this.taskWorkspaceStore.state();
      const splitViewOutput = project.outputs.find(
        (output) => output.name === splitViewOutputName,
      );
      assert(splitViewOutput, 'Project should have selected output defined');
      return splitViewOutput;
    }),
    shareReplay({ refCount: true }),
  );

  isPointCloudOutputSelected$ = this.selectedOutput$.pipe(
    map(isPointCloudOutput),
  );
  is2dOutputSelected$ = this.selectedOutput$.pipe(
    map((output) => isVideoOutput(output) || isImageAnnotationOutput(output)),
  );

  videoOutputName$ = combineLatest([
    this.taskWorkspaceStore('selectedOutputName').$,
    this.taskWorkspaceStore('splitViewOutputName').$,
  ]).pipe(
    map(
      ([selectedOutputName, splitViewOutputName]) =>
        splitViewOutputName ?? selectedOutputName,
    ),
    filter(
      (outputName) =>
        isDefined(outputName) &&
        isVideoOutput(
          getOutputByName(this.taskWorkspaceStore.state().project, outputName),
        ),
    ),
  );

  showProjectionSettings$ = this.videoOutputName$.pipe(
    map((videoOutputName) => {
      const { project } = this.taskWorkspaceStore.state();
      const output = getOutputByName(project, videoOutputName);
      assert(isVideoOutput(output), 'output should be a video output');

      const { task } = this.taskWorkspaceStore.state();
      const calibrationData = getCalibrationData(
        task,
        project.inputs,
        output.id,
      );

      return (
        isDefined(calibrationData) && !output.meta.is_orthographic_projection
      );
    }),
  );

  shouldShowCalibrationSettings$ = this.showProjectionSettings$.pipe(
    map(
      (showProjectionSettings) =>
        showProjectionSettings &&
        this.featureFlagService.hasFlag('camera-calibration-debug_on'),
    ),
  );

  pointCloudInjector$ = this.getPointCloudInjector$();

  isSplitViewEnabled =
    getTotalFusedOutputs(this.taskWorkspaceStore.state().project) > 1;

  constructor() {
    inject(CVToolbarKeyboardShortcutService).register();
  }

  private getPointCloudInjector$() {
    const fusionOutput = this.taskWorkspaceStore
      .state()
      .project.outputs.find(isSensorFusionOutput);

    if (!fusionOutput) {
      return NEVER;
    }

    this.fusionManagerFactory.getManager(fusionOutput.id);

    return from(
      this.fusionManagerFactory
        .getManager(fusionOutput.id)
        .sleepUntilPrimaryIsAvailable(),
    ).pipe(map((fusionMinion) => fusionMinion.injector));
  }
}
